/**
 * API client for VICIN GameBoy Edition.
 * Targets the same backend as the main app (api.vicin.app or localhost:8787 in dev).
 * Uses the same auth/magic-link flow.
 *
 * No backend code is touched — we only consume the existing public endpoints.
 */

const PROD_API = import.meta.env.VITE_API_URL || 'https://api.vicin.app';
const DEV_API = '/api'; // Vite proxy strips /api → localhost:8787

function apiBase(): string {
  if (import.meta.env.PROD) return PROD_API;
  return DEV_API;
}

export function getApiBase(): string {
  return apiBase();
}

const TOKEN_KEY = 'vicin-gb-token';
const REFRESH_KEY = 'vicin-gb-refresh';
const USER_KEY = 'vicin-gb-user';
const COMMUNITY_KEY = 'vicin-gb-community';

export interface User {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'owner' | 'resident' | 'member' | 'guest';
  unit: { id: string; code: string; type: string } | null;
  community_id?: string | null;
}

export interface Community {
  id: string;
  name: string;
  invite_code: string;
}

export class ApiError extends Error {
  status: number;
  code?: string;
  constructor(message: string, status: number, code?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

function readToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

function writeAuth(token: string, refresh: string | null, user: User, community: Community | null) {
  try {
    localStorage.setItem(TOKEN_KEY, token);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    if (community) localStorage.setItem(COMMUNITY_KEY, JSON.stringify(community));
  } catch { /* ignore */ }
}

function clearAuth() {
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(COMMUNITY_KEY);
    localStorage.removeItem('vicin_pending_email');
  } catch { /* ignore */ }
}

function readUser(): User | null {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) as User : null;
  } catch { return null; }
}

function readCommunity(): Community | null {
  try {
    const raw = localStorage.getItem(COMMUNITY_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function decodeJwtExp(token: string): number | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

async function request<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const url = `${apiBase()}${path}`;
  const token = readToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Client': 'vicin-gb/1.0',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { ...init, headers });
  const ct = res.headers.get('content-type') || '';
  const body = ct.includes('application/json') ? await res.json() : await res.text();

  if (!res.ok) {
    const errMsg = (body && typeof body === 'object' && 'error' in body)
      ? String((body as { error: unknown }).error)
      : `HTTP ${res.status}`;
    const code = (body && typeof body === 'object' && 'code' in body)
      ? String((body as { code: unknown }).code)
      : undefined;
    throw new ApiError(errMsg, res.status, code);
  }
  return body as T;
}

async function authedRequest<T = unknown>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return await request<T>(path, init);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      // try refresh once
      const refreshed = await tryRefresh();
      if (refreshed) {
        return request<T>(path, init);
      }
      clearAuth();
      window.dispatchEvent(new CustomEvent('vicin-gb:session-expired'));
    }
    throw err;
  }
}

// In-flight refresh promise, shared between tryRefresh() callers (the
// 401 interceptor and the bootstrap flow). Without this guard, both can
// race: each would POST /auth/refresh with the same token, the backend
// would rotate on the first call, the second call would fail with the
// now-revoked token, and the user would be logged out unnecessarily.
let refreshInFlight: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  const refresh = localStorage.getItem(REFRESH_KEY);
  if (!refresh) return false;
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    try {
      const res = await request<{ success?: boolean; token?: string; refresh_token?: string }>('/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (res?.success && res.token) {
        const user = readUser();
        const community = readCommunity();
        writeAuth(res.token, res.refresh_token ?? refresh, user!, community);
        return true;
      }
    } catch { /* fall through */ }
    return false;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/**
 * Boot-time auth bootstrap.
 *
 * Access tokens expire after 1h but refresh tokens last 30d. On PWA reopen,
 * the access token is usually expired — without this step, the synchronous
 * isAuthenticated() check would call clearAuth() and wipe the refresh token,
 * forcing the user to log in again from scratch.
 *
 * This function runs ONCE on app init:
 *   1. If there's a valid (non-expired) access token → no-op.
 *   2. If access token expired but refresh token exists → silently refresh.
 *   3. If refresh fails (or no refresh token) → clearAuth so the user lands
 *      on the login screen cleanly.
 *
 * Returns the new (or unchanged) auth state so the caller can hydrate the
 * Zustand store without an extra round-trip.
 */
export async function bootstrapAuth(): Promise<{
  authenticated: boolean;
  user: User | null;
  community: Community | null;
}> {
  const token = readToken();
  const refresh = localStorage.getItem(REFRESH_KEY);

  // No creds at all → unauthenticated.
  if (!token && !refresh) {
    return { authenticated: false, user: null, community: null };
  }

  // Access token present and not expired → already authenticated.
  if (token) {
    const exp = decodeJwtExp(token);
    if (exp === null || exp >= Date.now()) {
      const user = readUser();
      if (user) return { authenticated: true, user, community: readCommunity() };
      // Token valid but user record missing — fall through to refresh path.
    }
  }

  // Access token expired (or missing) — try to refresh.
  const refreshed = await tryRefresh();
  if (refreshed) {
    return {
      authenticated: true,
      user: readUser(),
      community: readCommunity(),
    };
  }

  // Refresh failed → wipe stale creds so the user lands cleanly on login.
  clearAuth();
  return { authenticated: false, user: null, community: null };
}

// ============================================================================
// Auth
// ============================================================================

export const authApi = {
  async requestCode(email: string): Promise<{ success: boolean; error?: string; code?: string; devCode?: string }> {
    const res = await request<{
      success: boolean;
      error?: string;
      code?: string;
      dev_mode?: boolean;
      message?: string;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email: email.toLowerCase().trim() }),
    });
    // En dev mode el backend devuelve `code` (6-char) en la respuesta
    if (res?.success && res.code) {
      return { success: true, devCode: res.code };
    }
    return res;
  },

  async verifyCode(email: string, code: string): Promise<{
    success: boolean;
    token?: string;
    refresh_token?: string;
    user?: User;
    community?: Community | null;
    needs_registration?: boolean;
    error?: string;
  }> {
    const data = await request<{
      success?: boolean;
      token?: string;
      refresh_token?: string;
      user?: { id: string; email: string; name: string | null; role: string; unit_code?: string | null };
      community?: Community | null;
      needs_registration?: boolean;
      error?: string;
    }>('/auth/verify-code', {
      method: 'POST',
      body: JSON.stringify({ email: email.toLowerCase().trim(), code: code.toUpperCase().trim() }),
    });
    if (data?.success && data.token && data.user) {
      const user: User = {
        id: data.user.id,
        email: data.user.email,
        name: data.user.name ?? null,
        role: data.user.role as User['role'],
        unit: data.user.unit_code ? { id: '', code: data.user.unit_code, type: '' } : null,
      };
      writeAuth(data.token, data.refresh_token ?? null, user, data.community ?? null);
      try { localStorage.setItem('vicin_pending_email', email.toLowerCase().trim()); } catch { /* */ }
      return { success: true, token: data.token, refresh_token: data.refresh_token, user, community: data.community ?? null };
    }
    return { success: false, needs_registration: data?.needs_registration, error: data?.error };
  },

  isAuthenticated(): boolean {
    const token = readToken();
    if (!token) {
      // No access token — fall back to refresh token. If we have one,
      // bootstrapAuth() will swap it for a fresh access token shortly
      // after the app loads, so treat the session as still alive.
      return Boolean(localStorage.getItem(REFRESH_KEY));
    }
    const exp = decodeJwtExp(token);
    if (exp !== null && exp < Date.now()) {
      // Access token expired but a refresh may still be possible —
      // return true optimistically and let `bootstrapAuth()` either
      // confirm (after a successful /auth/refresh) or downgrade to
      // false (after refresh fails and clearAuth runs). Wiping local
      // storage synchronously here would destroy the refresh token
      // before bootstrapAuth got a chance to use it.
      return Boolean(localStorage.getItem(REFRESH_KEY));
    }
    return Boolean(readUser());
  },

  getUser(): User | null {
    if (!this.isAuthenticated()) return null;
    return readUser();
  },
  getCommunity(): Community | null {
    if (!this.isAuthenticated()) return null;
    return readCommunity();
  },

  logout() {
    const token = readToken();
    // Fire-and-forget server-side invalidation; never block UI on this
    if (token) {
      try {
        fetch(`${apiBase()}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => { /* best-effort */ });
      } catch { /* */ }
    }
    clearAuth();
  },
};

// ============================================================================
// Door
// ============================================================================

export const doorApi = {
  async status(): Promise<{ configured: boolean }> {
    return authedRequest('/door/status');
  },
  async open(doorType: 'main' | 'garage' | 'pedestrian' = 'main'): Promise<{ success: boolean; error?: string }> {
    return authedRequest('/door/open', {
      method: 'POST',
      body: JSON.stringify({ door_type: doorType }),
    });
  },
};

// ============================================================================
// Announcements
// ============================================================================

export interface Announcement {
  id: string;
  title: string;
  body: string | null;
  priority: 'normal' | 'important' | 'urgent';
  created_at: string;
  author_name?: string | null;
}

export const announcementsApi = {
  async list(): Promise<{ announcements: Announcement[] }> {
    return authedRequest('/announcements');
  },
  async get(id: string): Promise<{ announcement: Announcement }> {
    return authedRequest(`/announcements/${id}`);
  },
  async create(payload: { title: string; content?: string; priority?: 'normal' | 'important'; send_push?: boolean }): Promise<{ success: boolean; announcement?: Announcement; error?: string }> {
    return authedRequest('/announcements', {
      method: 'POST',
      body: JSON.stringify({
        title: payload.title,
        content: payload.content || payload.title,
        priority: payload.priority === 'urgent' ? 'important' : (payload.priority ?? 'normal'),
        send_push: payload.send_push ?? false,
      }),
    });
  },
};

// ============================================================================
// Incidents
// ============================================================================

export interface Incident {
  id: string;
  title: string;
  description: string | null;
  status: 'reported' | 'in_progress' | 'resolved' | 'cancelled';
  category: string;
  urgency?: 'low' | 'medium' | 'high';
  created_at: string;
  updated_at?: string;
}

export const incidentsApi = {
  async list(): Promise<{ incidents: Incident[] }> {
    return authedRequest('/incidents');
  },
  async get(id: string): Promise<{ incident: Incident }> {
    return authedRequest(`/incidents/${id}`);
  },
  async create(payload: { title: string; description?: string; category: string; urgency?: 'low' | 'medium' | 'high' }): Promise<{ success: boolean; incident?: Incident; error?: string }> {
    const CATEGORY_MAP: Record<string, string> = {
      'plomería': 'plumbing',
      'electricidad': 'electrical',
      'limpieza': 'cleaning',
      'seguridad': 'security',
      'ruido': 'other',
      'otro': 'other',
      'ascensor': 'elevator',
      'estructura': 'structural',
      'jardín': 'garden',
    };
    const cat = CATEGORY_MAP[payload.category] ?? payload.category;
    return authedRequest('/incidents', {
      method: 'POST',
      body: JSON.stringify({
        title: payload.title,
        description: payload.description || payload.title,
        category: cat,
        urgency: payload.urgency ?? 'medium',
      }),
    });
  },
  async update(id: string, payload: Partial<{ title: string; description: string; status: Incident['status']; urgency: 'low' | 'medium' | 'high' }>): Promise<{ success: boolean; incident?: Incident; error?: string }> {
    return authedRequest(`/incidents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  },
};

// ============================================================================
// Votes
// ============================================================================

export interface VoteOption {
  text: string;
  votes: number;
  weight: number;
}

export interface Vote {
  id: string;
  title: string;
  description: string | null;
  ends_at: string;
  options: VoteOption[];
  status?: 'active' | 'closed';
  has_voted?: boolean;
  my_vote?: number | null;
  total_votes?: number;
  total_weight_cast?: number;
  total_eligible_weight?: number;
  quorum_reached?: boolean;
  winner_index?: number;
  is_ended?: boolean;
  can_vote?: boolean;
}

export const votesApi = {
  async list(): Promise<{ votes: Vote[] }> {
    return authedRequest('/votes');
  },
  async active(): Promise<{ votes: Vote[] }> {
    return authedRequest('/votes/active');
  },
  async get(id: string): Promise<{ vote: Vote }> {
    return authedRequest(`/votes/${id}`);
  },
  async cast(voteId: string, optionIndex: number): Promise<{ success: boolean; error?: string }> {
    return authedRequest(`/votes/${voteId}/cast`, {
      method: 'POST',
      body: JSON.stringify({ option_index: optionIndex }),
    });
  },
};

// ============================================================================
// Fees
// ============================================================================

export interface Fee {
  id: string;
  period: string;
  amount: number;
  total_amount?: number;
  base_amount?: number;
  extra_amount?: number;
  status: 'paid' | 'pending' | 'overdue' | 'partial';
  due_date: string;
  paid_at?: string | null;
  description?: string;
  unit_id?: string;
  unit_code?: string;
}

export const feesApi = {
  async list(): Promise<{ fees: Fee[]; total_pending?: number }> {
    const r = await authedRequest<{ pending?: Fee[]; fees?: Fee[]; total_pending?: number }>('/fees/my');
    const raw = r.fees ?? r.pending ?? [];
    const fees = raw.map((f) => ({
      ...f,
      amount: (f as any).amount ?? (f as any).total_amount ?? 0,
    }));
    return { fees, total_pending: r.total_pending };
  },
  async history(): Promise<{ payments: Fee[] }> {
    return authedRequest('/fees/my/history');
  },
  async get(id: string): Promise<{ fee: Fee }> {
    const r = await feesApi.list();
    const fee = r.fees.find((f) => f.id === id);
    if (!fee) throw new Error('FEE_NOT_FOUND');
    return { fee };
  },
  async markPaid(id: string, feeAmount: number, method: 'cash' | 'transfer' | 'bizum' | 'card' = 'cash'): Promise<{ success: boolean; payment_id?: string; error?: string }> {
    return authedRequest('/fees/payments', {
      method: 'POST',
      body: JSON.stringify({ fee_id: id, amount: feeAmount, method }),
    });
  },
};

// ============================================================================
// Notifications
// ============================================================================

export interface AppNotification {
  id: string;
  type: string;
  title: string;
  body: string | null;
  is_read: boolean;
  created_at: string;
}

export const notificationsApi = {
  async list(): Promise<{ notifications: AppNotification[]; unread_count?: number }> {
    return authedRequest('/notifications');
  },
  async unreadCount(): Promise<{ count: number }> {
    // Backend already returns unread_count alongside the list — no extra round-trip.
    const r = await authedRequest<{ unread_count?: number }>('/notifications?limit=1');
    return { count: r.unread_count ?? 0 };
  },
  async markRead(id: string): Promise<{ success: boolean }> {
    return authedRequest(`/notifications/${id}/read`, { method: 'PATCH' });
  },
  async markAllRead(): Promise<{ success: boolean }> {
    return authedRequest('/notifications/read-all', { method: 'POST' });
  },
};

// ============================================================================
// Visits / Doorbell
// ============================================================================

export interface RingEvent {
  id: string;
  visitor_name: string | null;
  visitor_note: string | null;
  status: 'ringing' | 'opened_door' | 'answered' | 'rejected' | 'timeout' | 'cancelled';
  created_at: string;
  expires_at?: string;
  unit_code?: string;
  answered_by_name?: string | null;
}

export const visitsApi = {
  async active(): Promise<{ rings: RingEvent[] }> {
    return authedRequest('/visits/rings/active');
  },
  async history(): Promise<{ rings: RingEvent[] }> {
    return authedRequest('/visits/rings/history');
  },
  async respond(ringId: string, action: 'open_door' | 'reject'): Promise<{ success: boolean; error?: string }> {
    return authedRequest(`/visits/rings/${ringId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  },
  /**
   * Poll messages for a ring. PUBLIC endpoint (no auth needed) but authed
   * works fine — extra Authorization header is ignored by the Worker.
   * Used by both visitor and resident chat panels.
   */
  async messages(ringId: string, since: string): Promise<{ success: boolean; ring: any; messages: any[]; server_ts: string }> {
    return authedRequest(`/visits/rings/${ringId}/messages?since=${encodeURIComponent(since)}`);
  },
  async acceptRing(ringId: string): Promise<{ success: boolean }> {
    return authedRequest(`/visits/rings/${ringId}/accept`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  async rejectRing(ringId: string): Promise<{ success: boolean }> {
    return authedRequest(`/visits/rings/${ringId}/reject`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  async endRing(ringId: string): Promise<{ success: boolean }> {
    return authedRequest(`/visits/rings/${ringId}/end`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  visitUrl(inviteCode: string, origin?: string): string {
    const base = origin ?? (typeof window !== 'undefined' ? window.location.origin : 'https://vicin.app');
    return `${base}/v/${inviteCode}`;
  },
};

// ============================================================================
// Voicemails — visitor's voice messages left after the resident didn't answer
// ============================================================================

export interface Voicemail {
  id: string;
  ring_id: string;
  unit_id: string;
  visitor_name: string | null;
  audio_mime: string;
  duration_ms: number | null;
  created_at: string;
  read_at: string | null;
  has_audio: boolean;
}

export const voicemailsApi = {
  /**
   * Unread count for the badge on HomeScreen. Same lightweight shape as
   * notificationsApi.unreadCount — just an integer. Polled frequently.
   */
  async unreadCount(): Promise<{ count: number }> {
    return authedRequest('/visits/voicemails/unread-count');
  },
  /** List voicemails for the user's unit, newest first. */
  async list(limit = 50): Promise<{ voicemails: Voicemail[] }> {
    return authedRequest(`/visits/voicemails/list?limit=${limit}`);
  },
  /** Fetch base64 audio bytes for a single voicemail (lazy-loaded). */
  async fetchAudio(id: string): Promise<{ audio_base64: string; audio_mime: string }> {
    return authedRequest(`/visits/voicemails/${encodeURIComponent(id)}/audio`);
  },
  /** Mark a voicemail as read. Idempotent on the server. */
  async markRead(id: string): Promise<{ success: boolean; read_at: string }> {
    return authedRequest(`/visits/voicemails/${encodeURIComponent(id)}/read`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
};
